import './WorkspaceDetailsPanel.scss'
import { createTableColumn, TableColumnDefinition, TableCellLayout, Button } from '@fluentui/react-components'
import { ArrowDownload24Regular, Delete24Regular, Document24Regular } from '@fluentui/react-icons'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataGridComponent } from '../DataGrid/DataGridComponent'
import { humanFileSize } from '../../util/humanFileSize'
import { MessagesStorageStorageDetailItemWorkspace } from '@/dotnet-client/models/MessagesStorageStorageDetailItemWorkspace'
import { MessagesStorageStorageFileType } from '@/dotnet-client/models/MessagesStorageStorageFileType'
import { ControlledDialog } from '../Dialog/ControlledDialog'
import { ENDPOINTS } from '@/util'
import { toast } from 'react-toastify'
import DOMPurify from 'isomorphic-dompurify'
import { getQueryString } from '@/dotnet-client/core/request'
import { useSessionStorage } from '@/hooks/useSessionStorage'

export const WorkspaceFilesPanel = () => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [searchParams] = useSearchParams()

  const [files, setFiles] = useState<MessagesStorageStorageDetailItemWorkspace[]>([])
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [selectedToDelete, setSelectedToDelete] = useState<MessagesStorageStorageDetailItemWorkspace>()
  const [loading, setLoading] = useState<boolean>(false)
  const [token] = useSessionStorage('token', '')

  const workspaceId = parseInt(searchParams.get('subscriptionId') ?? '')
  const [userRole] = useSessionStorage('roles', 1)

  const fetchFilesList = useCallback(async () => {
    dmpClient?.storageApi
      .getStorageApiGetFilesDetailsWorkspace(workspaceId)
      .then((result) => {
        setFiles(result)
      })
      .catch((err) => {
        console.warn(err)
      })
      .finally(() => {})
  }, [dmpClient?.storageApi, workspaceId])

  useEffect(() => {
    if (isDMPClientLoaded) {
      fetchFilesList()
    }
  }, [fetchFilesList, isDMPClientLoaded, workspaceId])

  const isDeletePermitted = useMemo(() => {
    if ((userRole & 16) > 0) return true
    return false
  }, [userRole])

  const onDownloadClick = async (f: MessagesStorageStorageDetailItemWorkspace) => {
    try {
      setLoading(true)

      const query = getQueryString({
        fileType: f.type,
        blobName: f.encryptedBlobName,
        subscriptionId: workspaceId,
      })
      const response = await fetch(`${ENDPOINTS.LANDING}/storage/download_file/workspace${query}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', f.filename ?? 'filename.txt')
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        let errMessage = 'Unknown error'
        if (response.statusText) errMessage = response.statusText
        try {
          const tryJson = await response.json()
          if (tryJson.Message) errMessage = tryJson.Message
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          // ignore
        }
        toast.error('Error downloading file: ' + DOMPurify.sanitize(errMessage))
      }
    } catch (error) {
      toast.error(`Error downloading file: ' + ${DOMPurify.sanitize(`${error}`)}`)
      console.error(DOMPurify.sanitize(`${error}`))
    } finally {
      setLoading(false)
    }
  }

  const onDeleteClick = async (f: MessagesStorageStorageDetailItemWorkspace) => {
    setLoading(true)

    dmpClient?.storageApi
      .postStorageApiDeleteFileWorkspace(f.encryptedBlobName, workspaceId, f.type)
      .then((res) => {
        if (res) toast.success('File deleted.')
        else toast.warn('File was not deleted.')
      })
      .catch((error) => {
        toast.error(`Error deleting file: ' + ${DOMPurify.sanitize(`${error}`)}`)
        console.error(DOMPurify.sanitize(`${error}`))
      })
      .finally(() => {
        fetchFilesList()
        setLoading(false)
      })
  }

  const columns: TableColumnDefinition<MessagesStorageStorageDetailItemWorkspace>[] = [
    createTableColumn<MessagesStorageStorageDetailItemWorkspace>({
      columnId: 'filename',
      compare: (a, b) => {
        return a.filename.localeCompare(b.filename)
      },
      renderHeaderCell: () => {
        return 'Filename'
      },
      renderCell: (item) => {
        return (
          <TableCellLayout
            media={
              <Document24Regular color={item.type === MessagesStorageStorageFileType.Bin ? '#979797' : '#D04A02'} />
            }
          >
            <div
              style={{
                flexDirection: 'column',
                display: 'flex',
                paddingTop: '.5rem',
                paddingBottom: '.5rem',
                gap: '5px',
                lineHeight: '1rem',
              }}
            >
              <span style={{ fontSize: '16px' }}>{item.filename}</span>
              <span style={{ color: '#979797', fontSize: 'small' }}>{item.blobName}</span>
            </div>
          </TableCellLayout>
        )
      },
    }),
    createTableColumn<MessagesStorageStorageDetailItemWorkspace>({
      columnId: 'size',
      compare: (a, b) => {
        return a.contentType.localeCompare(b.contentType)
      },
      renderHeaderCell: () => {
        return 'Size'
      },
      renderCell: (item: MessagesStorageStorageDetailItemWorkspace) => {
        return <TableCellLayout>{humanFileSize(item.size)}</TableCellLayout>
      },
    }),
    createTableColumn<MessagesStorageStorageDetailItemWorkspace>({
      columnId: 'properties',
      compare: (a, b) => {
        return a.contentType.localeCompare(b.contentType)
      },
      renderHeaderCell: () => {
        return 'Properties'
      },
      renderCell: (item: MessagesStorageStorageDetailItemWorkspace) => {
        return (
          <TableCellLayout>
            {Object.keys(item.properties).map((p) => (
              <>
                <div className="pillContainer">
                  <div className="pillLeft">{p}</div>
                  <div className="pillRight">{item.properties[p]}</div>
                </div>
              </>
            ))}
          </TableCellLayout>
        )
      },
    }),
    //TODO! - @RM do size column
    createTableColumn<MessagesStorageStorageDetailItemWorkspace>({
      columnId: 'actions',
      renderHeaderCell: () => {
        return ''
        //return "Actions";
      },
      renderCell: (item) => {
        return (
          <>
            <Button
              appearance="transparent"
              icon={<ArrowDownload24Regular />}
              aria-label="Download File"
              onClick={() => onDownloadClick(item)}
              disabled={loading}
            />
            <Button
              appearance="transparent"
              icon={<Delete24Regular />}
              aria-label="Delete File"
              onClick={() => {
                setSelectedToDelete(item)
                setIsDeleteDialogOpen(true)
              }}
              disabled={loading || !isDeletePermitted}
            />
          </>
        )
      },
    }),
  ]

  return (
    <>
      <DataGridComponent
        items={files}
        columns={columns}
        heightAdjustment={90}
        // onAdd={() => setIsAddOpen(true)}
      />
      {selectedToDelete && (
        <ControlledDialog
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          title={'Delete file'}
          content={`Please confirm you want to delete ${selectedToDelete.blobName} (${selectedToDelete.filename})?`}
          onConfirm={() => {
            onDeleteClick(selectedToDelete)
            setIsDeleteDialogOpen(false)
          }}
        />
      )}
    </>
  )
}
